import PropTypes from 'prop-types';
import React from 'react';
import { Box, Button, Chip, Paper, TextField, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';

import colors from 'common/constants/colors';
import { AssociationStatusLabel, SigLengthLimits } from 'enums/SigManagement.enum';

/**
 * Which sig is being managed, whether it is active, and the two operations that act on the sig
 * itself rather than on its associations.
 *
 * Editing happens in place, not in a second dialog. A dialog on top of a dialog hides the very
 * thing being edited, and the wording of a sig is almost always changed by comparing it to what it
 * says now — so the original stays on screen beside the field, read-only, for exactly as long as
 * the edit is open.
 *
 * The edited text is staged like every other change rather than saved on the spot, so one commit
 * applies the sig and its associations together and there is one thing to undo rather than two
 * kinds of thing.
 *
 * While a sig is being created there is no existing wording to compare to, no status to show and
 * nothing to deactivate, so the field stands alone and the editor is always open — the same
 * validation, with nothing on screen that refers to a record that does not exist yet.
 */
const SigSummarySection = ({
  sig,
  sigStatus,
  editedSigText,
  isEditing,
  isCreating,
  canDeactivate,
  saving,
  onStartEditing,
  onStopEditing,
  onEditedSigTextChange,
  onToggleStatus,
}) => {
  // Both ends, and read from the shared limits rather than a local literal. This field only had a
  // minimum, so the inline editor accepted a sig the Add Sig form would have refused and the server
  // now rejects.
  const trimmedLength = editedSigText.trim().length;
  const tooShort = trimmedLength < SigLengthLimits.SIG_MIN;
  const tooLong = trimmedLength > SigLengthLimits.SIG_MAX;
  // An untouched create form is empty, and empty is not yet wrong. Flagged red before the user has
  // typed a character, the field accuses them of a mistake they have not made.
  const isUntouchedNewSig = isCreating && trimmedLength === 0;
  const isInvalid = (tooShort || tooLong) && !isUntouchedNewSig;

  const lengthHelperText = () => {
    if (isUntouchedNewSig) {
      return 'Enter the sig wording';
    }

    if (tooShort) {
      return `A sig needs at least ${SigLengthLimits.SIG_MIN} characters`;
    }

    if (tooLong) {
      return `A sig cannot be longer than ${SigLengthLimits.SIG_MAX} characters`;
    }

    return `${trimmedLength} / ${SigLengthLimits.SIG_MAX} characters`;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${colors.cardBorderGrey}`,
        bgcolor: colors.white,
        px: 2.5,
        py: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
            Sig
          </Typography>
          {!isCreating && (
            <Chip
              size='small'
              variant={sigStatus ? 'filled' : 'outlined'}
              color={sigStatus ? 'success' : 'default'}
              label={sigStatus ? AssociationStatusLabel.ACTIVE : AssociationStatusLabel.INACTIVE}
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>

        {/* Neither operation exists for a sig that does not: there is no saved wording to edit back
            to, and nothing to deactivate. A new sig is created active. */}
        {!isCreating && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEditing ? (
              <Button
                variant='outlined'
                color='inherit'
                startIcon={<CloseIcon />}
                onClick={onStopEditing}
              >
                Close editor
              </Button>
            ) : (
              <Button variant='outlined' startIcon={<EditIcon />} onClick={onStartEditing}>
                Edit
              </Button>
            )}
            <Button
              variant='outlined'
              color={sigStatus ? 'error' : 'success'}
              startIcon={sigStatus ? <BlockIcon /> : <CheckCircleOutlineIcon />}
              disabled={!canDeactivate || saving}
              onClick={onToggleStatus}
            >
              {sigStatus ? 'Deactivate' : 'Activate'}
            </Button>
          </Box>
        )}
      </Box>

      {!isEditing && !isCreating && (
        <Typography variant='body1' sx={{ mt: 1.5, wordBreak: 'break-word' }}>
          {sig}
        </Typography>
      )}

      {(isEditing || isCreating) && (
        <Box
          sx={{
            display: 'grid',
            // One full-width column while creating. The second column exists only to hold the
            // original wording, and there is none, so splitting the row would leave the field at
            // half width beside an empty box.
            gridTemplateColumns: isCreating ? '1fr' : { xs: '1fr', md: '1fr 1fr' },
            gap: 2.5,
            mt: 2,
          }}
        >
          <Box>
            <Typography variant='caption' color='text.secondary'>
              {isCreating ? 'New sig' : 'Edit sig'}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={editedSigText}
              onChange={(event) => onEditedSigTextChange(event.target.value)}
              error={isInvalid}
              helperText={lengthHelperText()}
              // No maxLength: it truncates a paste silently, and it would also stop a legacy sig
              // longer than the limit from being shortened here — which is the only way to fix one.
              inputProps={{ 'aria-label': isCreating ? 'New sig' : 'Edit sig' }}
              sx={{ mt: 0.5 }}
            />
          </Box>

          {/* Read-only, and deliberately still here: the change being made is almost always a
              wording change, and judging one without the previous wording in view is guesswork. */}
          {!isCreating && (
            <Box>
              <Typography variant='caption' color='text.secondary'>
                Original
              </Typography>
              <Box
                sx={{
                  mt: 0.5,
                  p: 1.5,
                  borderRadius: 1.5,
                  border: `1px solid ${colors.cardBorderGrey}`,
                  bgcolor: colors.dialogSurfaceGrey,
                }}
              >
                <Typography variant='body2' color='text.secondary' sx={{ wordBreak: 'break-word' }}>
                  {sig}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default SigSummarySection;

SigSummarySection.propTypes = {
  // Optional, and only because a sig being created does not have one yet. Everything that reads it
  // is behind `!isCreating`.
  sig: PropTypes.string,
  sigStatus: PropTypes.bool,
  editedSigText: PropTypes.string,
  isEditing: PropTypes.bool,
  isCreating: PropTypes.bool,
  canDeactivate: PropTypes.bool,
  saving: PropTypes.bool,
  // The edit toggle and the status flip do not exist while creating, so neither do their handlers.
  onStartEditing: PropTypes.func,
  onStopEditing: PropTypes.func,
  onEditedSigTextChange: PropTypes.func.isRequired,
  onToggleStatus: PropTypes.func,
};

SigSummarySection.defaultProps = {
  sig: '',
  sigStatus: false,
  editedSigText: '',
  isEditing: false,
  isCreating: false,
  canDeactivate: false,
  saving: false,
  onStartEditing: undefined,
  onStopEditing: undefined,
  onToggleStatus: undefined,
};
